import isValid from 'date-fns/isValid';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addWeeks from 'date-fns/addWeeks';
import isAfter from 'date-fns/isAfter';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import subWeeks from 'date-fns/subWeeks';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/free-solid-svg-icons';

import { DAILY, MONTHLY, WEEKLY } from '../../Constants/Signal';

export default memo(UtilizationTimeSelectModal);

const aggregateIntervalMap = new Map([
  [DAILY, 'day'],
  [MONTHLY, 'month'],
  [WEEKLY, 'week'],
]);

function UtilizationTimeSelectModal(props) {
  const { handleUtilizationTimeSelect, settings } = props;
  const now = useMemo(() => new Date(), []);

  const [state, updateState] = useState(() => {
    const end = props.customEndTimestamp
        ? new Date(props.customEndTimestamp)
        : null,
      start = props.customStartTimestamp
        ? new Date(props.customStartTimestamp)
        : null;

    return {
      endDate: isValid(end) ? end : null,
      startDate: isValid(start) ? start : null,
    };
  });

  const helperText = useMemo(() => {
      switch (props.settings?.aggregation) {
        case DAILY:
          return 'Choose a window of up to 7 days to display.';
        case MONTHLY:
          return 'Choose a window of up to 6 months to display.';
        case WEEKLY:
          return 'Choose a window of up to 6 weeks to display.';
        default:
          return '';
      }
    }, [props.settings?.aggregation]),
    startMinDate = useMemo(() => {
      if (!isValid(state.endDate)) return null;

      switch (settings?.aggregation) {
        case DAILY:
          return subDays(state.endDate, 7);
        case WEEKLY:
          return subWeeks(state.endDate, 5);
        case MONTHLY:
          return subMonths(state.endDate, 5);
      }
    }, [settings?.aggregation, state.endDate]),
    endMaxDate = useMemo(() => {
      if (!isValid(state.startDate)) return now;

      let maxDate;
      switch (settings?.aggregation) {
        case DAILY:
          maxDate = addDays(state.startDate, 7);
          break;
        case WEEKLY:
          maxDate = addWeeks(state.startDate, 5);
          break;
        case MONTHLY:
          maxDate = addMonths(state.startDate, 5);
          break;
      }

      return isAfter(maxDate, now) ? now : maxDate;
    }, [now, settings?.aggregation, state.startDate]);

  const handleChangeEnd = useCallback((endDate) => {
      updateState((prevState) => ({ ...prevState, endDate }));
    }, []),
    handleChangeStart = useCallback((startDate) => {
      updateState((prevState) => ({ ...prevState, startDate }));
    }, []),
    handleUpdate = useCallback(() => {
      const dateStop = state.endDate ?? new Date(),
        interval = aggregateIntervalMap.get(settings?.aggregation);
      let dateStart = state.startDate;
      if (!dateStart) {
        switch (settings?.aggregation) {
          case DAILY:
            dateStart = subDays(dateStop, 7);
            break;
          case WEEKLY:
            dateStart = subWeeks(dateStop, 5);
            break;
          case MONTHLY:
            dateStart = subMonths(dateStop, 5);
            break;
        }
      }

      handleUtilizationTimeSelect(dateStart, dateStop, interval);
    }, [
      handleUtilizationTimeSelect,
      settings?.aggregation,
      state.endDate,
      state.startDate,
    ]);

  return (
    <Modal centered isOpen={props.isOpen} toggle={props.toggleModal}>
      <ModalHeader className="border-bottom-0" toggle={props.toggleModal} />
      <ModalBody className="container-fluid py-0">
        <div className="row">
          <p className="col-12 modal-supplemental-text">{helperText}</p>
        </div>
        <div className="row align-items-stretch">
          <div className="form-group col-5">
            <label className="modal-inputlabel-text" htmlFor="startDate">
              Start Date
            </label>
            <DatePicker
              autoComplete="off"
              className="form-control"
              endDate={state.endDate}
              minDate={startMinDate}
              maxDate={state.endDate ?? now}
              name="startDate"
              onChange={handleChangeStart}
              placeholderText="From"
              selected={state.startDate}
              selectsStart
              startDate={state.startDate}
              withPortal
            />
          </div>
          <div className="col-2 d-flex align-items-center justify-content-center">
            <FontAwesomeIcon icon={faMinus} />
          </div>
          <div className="form-group col-5">
            <label className="modal-inputlabel-text" htmlFor="endDate">
              End Date
            </label>
            <DatePicker
              autoComplete="off"
              className="form-control"
              endDate={state.endDate}
              name="endDate"
              onChange={handleChangeEnd}
              maxDate={endMaxDate}
              minDate={state.startDate}
              placeholderText="To"
              selected={state.endDate}
              selectsEnd
              startDate={state.startDate}
              withPortal
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="border-top-0">
        <Button
          className="py-0 px-3 mr-3 three-quarter-text"
          onClick={props.toggleModal}
          outline
        >
          Cancel
        </Button>
        <Button
          className="modal-button-submit py-0 px-4 three-quarter-text"
          onClick={handleUpdate}
        >
          Set Time Frame
        </Button>
      </ModalFooter>
    </Modal>
  );
}

UtilizationTimeSelectModal.propTypes = {
  customEndTimestamp: PropTypes.number,
  customStartTimestamp: PropTypes.number,
  handleUtilizationTimeSelect: PropTypes.func,
  isOpen: PropTypes.bool,
  settings: PropTypes.object,
  toggleModal: PropTypes.func,
};
